import { api } from '@/http/constants';
import { getRequestHeaders } from '@/http/fetchWrapper';
import type {
  MfaSendEmailCodeRequest,
  MfaSendEmailCodeResponse,
} from '@eye-able/types';
import { useMutation } from '@tanstack/react-query';
import { mfaErrorFromStatus } from './mfaError';

export interface SentEmailCode {
  /** The earliest time the next code may be asked for. */
  resendAt: Date;
}

/**
 * Asks the API to mail a code, to log in with or to set the method up. A raw
 * fetch: a 429 carries the time the next code may be asked for, which is an
 * answer, not an error.
 */
export function useSendMfaEmailCode() {
  return useMutation({
    mutationFn: async (
      body: MfaSendEmailCodeRequest,
    ): Promise<SentEmailCode> => {
      const response = await fetch(api.mfaEmailCode, {
        mode: 'cors',
        method: 'POST',
        cache: 'no-cache',
        credentials: import.meta.env.PROD ? 'include' : 'omit',
        headers: getRequestHeaders(api.mfaEmailCode),
        body: JSON.stringify(body),
      });
      if (response.status === 429) {
        const { resendAt } = (await response.json()) as { resendAt?: string };
        if (resendAt) return { resendAt: new Date(resendAt) };
      }
      if (!response.ok) {
        throw mfaErrorFromStatus(response.status);
      }
      const sent = (await response.json()) as MfaSendEmailCodeResponse;
      return { resendAt: new Date(sent.resendAt) };
    },
  });
}
